<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div
    class="layout--main"
    :class="[
      layoutTypeClass,
      navbarClasses,
      footerClasses,
      { 'no-scroll': isAppPage }
    ]"
  >
    <v-nav-menu
      :nav-menu-items="navMenuItems"
      title=""
      parent=".layout--main"
    />

    <div
      id="content-area"
      :class="[contentAreaClass, { 'show-overlay': bodyOverlay }]"
    >
      <div id="content-overlay" />

      <!-- Navbar -->
      <template v-if="mainLayoutType === 'horizontal' && windowWidth >= 1200">
        <the-navbar-horizontal
          :navbar-type="navbarType"
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark }
          ]"
        />

        <div v-if="navbarType === 'static'" style="height: 62px" />

        <h-nav-menu
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark }
          ]"
          :nav-menu-items="navMenuItems"
        />
      </template>

      <template v-else>
        <the-navbar-vertical
          :navbar-color="navbarColor"
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark }
          ]"
        />
      </template>
      <!-- /Navbar -->

      <div class="content-wrapper">
        <div class="router-view pr-0 pl-0">
          <div class="router-content">
            <transition :name="routerTransition">
              <div
                v-if="$route.meta.breadcrumb || $route.meta.pageTitle"
                class="router-header flex flex-wrap items-center mb-6"
              >
                <div
                  class="content-area__heading"
                  :class="{
                    'pr-4 border-0 md:border-r border-solid border-grey-light':
                      $route.meta.breadcrumb
                  }"
                >
                  <h2 class="mb-1">
                    {{ routeTitle }}
                  </h2>
                </div>

                <!-- BREADCRUMB -->
                <vx-breadcrumb
                  v-if="$route.meta.breadcrumb"
                  class="ml-4 md:block hidden"
                  :route="$route"
                  :is-r-t-l="$vs.rtl"
                />

                <!-- DROPDOWN -->
                <vs-dropdown
                  vs-trigger-click
                  class="ml-auto md:block hidden cursor-pointer"
                >
                  <vs-button radius icon="icon-settings" icon-pack="feather" />

                  <vs-dropdown-menu class="w-32">
                    <vs-dropdown-item>
                      <div
                        class="flex items-center"
                        @click="$router.push('/pages/profile').catch(() => {})"
                      >
                        <feather-icon
                          icon="UserIcon"
                          class="inline-block mr-2"
                          svg-classes="w-4 h-4"
                        />
                        <span>Profile</span>
                      </div>
                    </vs-dropdown-item>
                    <vs-dropdown-item>
                      <div
                        class="flex items-center"
                        @click="$router.push('/apps/todo').catch(() => {})"
                      >
                        <feather-icon
                          icon="CheckSquareIcon"
                          class="inline-block mr-2"
                          svg-classes="w-4 h-4"
                        />
                        <span>Tasks</span>
                      </div>
                    </vs-dropdown-item>
                    <vs-dropdown-item>
                      <div
                        class="flex items-center"
                        @click="$router.push('/apps/email').catch(() => {})"
                      >
                        <feather-icon
                          icon="MailIcon"
                          class="inline-block mr-2"
                          svg-classes="w-4 h-4"
                        />
                        <span>Inbox</span>
                      </div>
                    </vs-dropdown-item>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </transition>

            <div class="content-area__content">
              <back-to-top
                v-if="!hideScrollToTop"
                bottom="5%"
                :right="$vs.rtl ? 'calc(100% - 2.2rem - 38px)' : '30px'"
                visibleoffset="500"
              >
                <vs-button
                  icon-pack="fas"
                  icon="fa-chevron-up"
                  class="shadow-lg btn-back-to-top"
                />
              </back-to-top>

              <transition :name="routerTransition" mode="out-in">
                <router-view
                  @changeRouteTitle="changeRouteTitle"
                  @setAppClasses="
                    classesStr => $emit('setAppClasses', classesStr)
                  "
                />
              </transition>
            </div>
          </div>
        </div>
      </div>
      <the-footer />
    </div>
    <div v-if="popupActive" class="phone-alert">
      <div class="flex flex-row-reverse">
        <button
          class="border-none bg-white bg-opacity-0 text-lg cursor-pointer"
          @click="ignoreCall"
        >
          <i class="far fa-times-circle"></i>
        </button>
      </div>
      <div class="phone-alert-title">
        <i class="fas fa-phone-alt"></i>Neuer Anruf
      </div>
      <div class="phone-alert-details">
        <div class="phone-alert-details-name">
          {{ callInfo.salutation }} {{ callInfo.firstName }}
          {{ callInfo.lastName }}
        </div>
        <div
          v-if="
            callInfo.company.name && callInfo.type !== 'COMPANY_CONTACT_PARTNER'
          "
          class="phone-alert-details-company"
        >
          {{ callInfo.company.name }}
        </div>
        <div class="phone-alert-details-phone">{{ callInfo.number }}</div>
      </div>
      <div class="phone-alert-buttons">
        <vs-button class="px-24" @click="openOffcanvas">
          Notiz anlegen
        </vs-button>
      </div>
    </div>

    <vs-sidebar
      v-model="active"
      color="primary"
      class="phone-sidebar"
      position-right
      :hidden-background="true"
      :click-not-close="true"
    >
      <template #header>
        <button @click="active = false"><i class="fal fa-times"></i></button>
        <div class="phone-sidebar-name">
          <router-link :to="editRoute(callInfo.referencedId, call.refType)">
            {{ callInfo.salutation }} {{ callInfo.firstName }}
            {{ callInfo.lastName }}
          </router-link>
        </div>
        <div
          v-if="callInfo.company.name && callInfo.type !== 'COMPANY'"
          class="phone-sidebar-company"
        >
          <i class="fas fa-building"></i>{{ callInfo.company.name }}
        </div>
        <div class="phone-sidebar-phone">
          <i class="fas fa-phone-alt"></i>{{ callInfo.number }}
        </div>
      </template>
      <ValidationObserver ref="phoneForm" tag="form">
        <DataInput
          v-model="call.typeID"
          type="model"
          class="w-full mt-4"
          label="Art"
          name="typeID"
          model-label="nameHistory"
          :select-label="el => el.nameHistory"
          gql-query="actionTypes"
          gql-single-query="actionType"
        />
        <DataInput
          v-model="call.description"
          required
          type="html"
          class="w-full mt-4"
          label="Beschreibung"
          name="description"
        />
        <vs-button class="mt-4" @click="submit">Telefonat anlegen</vs-button>
      </ValidationObserver>
    </vs-sidebar>
  </div>
</template>

<script>
import BackToTop from "vue-backtotop";
import HNavMenu from "@/layouts/components/horizontal-nav-menu/HorizontalNavMenu.vue";
import navMenuItems from "@/layouts/components/vertical-nav-menu/navMenuItems.js";
import TheNavbarHorizontal from "@/layouts/components/navbar/TheNavbarHorizontal.vue";
import TheNavbarVertical from "@/layouts/components/navbar/TheNavbarVertical.vue";
import TheFooter from "@/layouts/components/TheFooter.vue";
import themeConfig from "@/../themeConfig.js";
import VNavMenu from "@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue";
import gql from "graphql-tag";
import DataInput from "@/components/ui/DataInput";

export default {
  components: {
    BackToTop,
    HNavMenu,
    TheFooter,
    TheNavbarHorizontal,
    TheNavbarVertical,
    VNavMenu,
    DataInput
  },
  data() {
    return {
      footerType: themeConfig.footerType || "static",
      hideScrollToTop: themeConfig.hideScrollToTop,
      isNavbarDark: false,
      navbarColor: themeConfig.navbarColor || "#fff",
      navbarType: themeConfig.navbarType || "floating",
      routerTransition: themeConfig.routerTransition || "none",
      routeTitle: this.$route.meta.pageTitle,
      active: false,
      call: {
        typeID: "4eb4c1a4-02ca-4751-ba45-892a1f12b509",
        description: null,
        refType: null
      },
      callInfo: {
        salutation: null,
        firstName: null,
        lastName: null,
        number: null,
        type: null,
        callId: null,
        referencedId: null,
        company: {
          name: null,
          id: null
        }
      },
      popupActive: false,
      currentlyConnectingToSocket: false,
      intentionalDisconnect: false
    };
  },
  computed: {
    navMenuItems() {
      let items = navMenuItems.slice();
      if (
        this.applicantFilterPresets &&
        this.applicantFilterPresets.length > 0
      ) {
        for (const i in items) {
          let item = items[i];
          if (item.items) {
            for (const j in item.items) {
              let subItem = item.items[j];
              if (subItem.slug === "applicants") {
                items[i].items[j].submenu = [
                  {
                    url: "/applicants",
                    name: "Alle",
                    slug: "applicants-all"
                  }
                ];

                this.applicantFilterPresets.forEach(el => {
                  items[i].items[j].submenu.push({
                    url: `/applicants/${el.id}`,
                    name: el.name,
                    slug: `applicants-filter-${el.id}`
                  });
                });
              }
            }
          }
        }
      }

      return items;
    },
    bodyOverlay() {
      return this.$store.state.bodyOverlay;
    },
    contentAreaClass() {
      if (this.mainLayoutType === "vertical") {
        if (this.verticalNavMenuWidth == "default")
          return "content-area-reduced";
        else if (this.verticalNavMenuWidth == "reduced")
          return "content-area-lg";
        else return "content-area-full";
      }
      // else if(this.mainLayoutType === "boxed") return "content-area-reduced"
      else return "content-area-full";
    },
    footerClasses() {
      return {
        "footer-hidden": this.footerType == "hidden",
        "footer-sticky": this.footerType == "sticky",
        "footer-static": this.footerType == "static"
      };
    },
    isAppPage() {
      return this.$route.meta.no_scroll;
    },
    isThemeDark() {
      return this.$store.state.theme == "dark";
    },
    layoutTypeClass() {
      return `main-${this.mainLayoutType}`;
    },
    mainLayoutType() {
      return this.$store.state.mainLayoutType;
    },
    navbarClasses() {
      return {
        "navbar-hidden": this.navbarType == "hidden",
        "navbar-sticky": this.navbarType == "sticky",
        "navbar-static": this.navbarType == "static",
        "navbar-floating": this.navbarType == "floating"
      };
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth;
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    }
  },
  mounted() {
    //this.connectTapiSocket();
    //window.onbeforeunload = () => {
    //  this.intentionalDisconnect = true;
    //  window.TapiWebSocket.close(1000, "leaving page");
    //};
    //setInterval(() => {
    //  if (
    //    !window.TapiWebSocket ||
    //    window.TapiWebSocket.readyState === WebSocket.CLOSED ||
    //    window.TapiWebSocket.readyState === WebSocket.CLOSING
    //  ) {
    //    console.log("socket was closed");
    //    window.TapiWebSocket = null;
    //    this.connectTapiSocket();
    //  }
    //}, 1000);
  },
  apollo: {
    applicantFilterPresets: {
      query: gql`
        query ApplicantFilterPresets {
          applicantFilterPresets {
            id
            name
          }
        }
      `
    },
    me: gql`
      query {
        me {
          id
          consultant {
            id
          }
        }
      }
    `
  },
  watch: {
    $route() {
      this.routeTitle = this.$route.meta.pageTitle;
    },
    isThemeDark(val) {
      const color = this.navbarColor == "#fff" && val ? "#10163a" : "#fff";
      this.updateNavbarColor(color);
    },
    "$store.state.mainLayoutType"(val) {
      this.setNavMenuVisibility(val);
    }
  },
  created() {
    const color =
      this.navbarColor == "#fff" && this.isThemeDark
        ? "#10163a"
        : this.navbarColor;
    this.updateNavbarColor(color);
    this.setNavMenuVisibility(this.$store.state.mainLayoutType);
  },
  beforeUnmount() {
    this.intentionalDisconnect = true;
    window.TapiWebSocket.close(1000, "leaving page");
  },
  methods: {
    ignoreCall() {
      this.popupActive = false;
      this.$apollo.mutate({
        mutation: gql`
          mutation UpdateTapiCall($input: TapiCallInput) {
            updateTapiCall(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            id: this.callInfo.id,
            callAction: "COMPLETED"
          }
        }
      });
    },
    openOffcanvas() {
      this.active = true;
      this.popupActive = false;
      console.log(this.me.id);
    },
    // async connectTapiSocket() {
    //   if (!this.currentlyConnectingToSocket) {
    //     this.currentlyConnectingToSocket = true;
    //   } else {
    //     //for some reason this never gets set
    //     setTimeout(() => (this.currentlyConnectingToSocket = false), 2000);
    //     return;
    //   }
    //   if (!window.TapiWebSocket) {
    //     let ticketResponse = await this.$apollo.query({
    //       query: gql`
    //         query {
    //           webSocketTicket
    //         }
    //       `,
    //       fetchPolicy: "network-only"
    //     });
    //     window.TapiWebSocket = new WebSocket(
    //       "wss://promatch-api.select-gmbh.de/socket?auth_ticket=" +
    //         ticketResponse.data.webSocketTicket
    //     );
    //   }
    //   window.TapiWebSocket.onmessage = async event => {
    //     if (event.data === "PING") {
    //       window.TapiWebSocket.send("PONG");
    //       return;
    //     }
    //     let callInfo = JSON.parse(event.data);
    //     console.log(callInfo);
    //     if (callInfo.direction === "INCOMING") {
    //       this.popupActive = true;
    //       const addr = callInfo.from;
    //       this.callInfo.firstName = addr.firstName;
    //       this.callInfo.lastName = addr.lastName;
    //       this.callInfo.type = addr.referencedType;
    //       this.callInfo.number = addr.originalPhoneNumber;
    //       this.callInfo.id = callInfo.callID;
    //       this.callInfo.referencedId = addr.referencedID;
    //       this.call.refID = addr.referencedID;
    //       this.call.refType = "APPLICANT";
    //       if (addr.referencedType === "COMPANY") {
    //         this.callInfo.company = {
    //           id: addr.referencedID,
    //           name: addr.Company
    //         };
    //         this.call.refType = "COMPANY";
    //       } else if (addr.referencedType === "COMPANY_CONTACT_PARTNER") {
    //         const ccp = await this.$apollo.query({
    //           query: gql`
    //             query CompanyContactPartner($companyContactPartnerId: ID!) {
    //               companyContactPartner(id: $companyContactPartnerId) {
    //                 company {
    //                   id
    //                   name
    //                 }
    //               }
    //             }
    //           `,
    //           variables: {
    //             companyContactPartnerId: addr.referencedID
    //           }
    //         });
    //         this.callInfo.company = ccp.data.company;
    //         this.call.refType = "COMPANY";
    //       }
    //     }
    //   };
    //   window.TapiWebSocket.onerror = event => {
    //     console.log(event);
    //     window.TapiWebSocket.close(
    //       1000 /*ProtocolError*/,
    //       JSON.stringify(event.data)
    //     );
    //   };
    //   window.TapiWebSocket.onclose = () => {
    //     window.TapiWebSocket = null;
    //     if (!this.intentionalDisconnect) {
    //       this.connectTapiSocket();
    //     }
    //   };
    //   this.currentlyConnectingToSocket = false;
    // },
    changeRouteTitle(title) {
      this.routeTitle = title;
    },
    updateNavbarColor(val) {
      this.navbarColor = val;
      if (val == "#fff") this.isNavbarDark = false;
      else this.isNavbarDark = true;
    },
    setNavMenuVisibility(layoutType) {
      if (
        (layoutType === "horizontal" && this.windowWidth >= 1200) ||
        (layoutType === "vertical" && this.windowWidth < 1200)
      ) {
        this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", false);
        this.$store.dispatch("updateVerticalNavMenuWidth", "no-nav-menu");
      } else {
        this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", true);
      }
    },
    async submit() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation CreateAction($input: NewAction!) {
              createAction(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: {
              typeID: this.call.typeID,
              description: this.call.description,
              done: true,
              refType: this.call.refType,
              refID: this.callInfo.referencedId,
              consultantID: this.me.consultant.id
            }
          }
        })
        .then(({ data }) => {
          this.$apollo.mutate({
            mutation: gql`
              mutation UpdateTapiCall($input: TapiCallInput) {
                updateTapiCall(input: $input) {
                  id
                }
              }
            `,
            variables: {
              input: {
                id: this.callInfo.id,
                callAction: "COMPLETED",
                actionID: data.createAction.id
              }
            }
          });
          console.log(data);
          this.active = false;
          this.popupActive = false;
        });
    },
    editRoute(id, type) {
      if (type == "COMPANY") {
        return `/company/${id}/edit`;
      } else {
        return `/applicant/${id}/show`;
      }
    }
  }
};
</script>
